import Phaser from 'phaser';

/**
 * CloudManager
 * Manages animated cloud shadows that drift across the battlefield
 * Uses cloud texture sprites with varied scale and rotation for natural appearance
 */
export class CloudManager {
  constructor(scene, battlefieldSize = 'small', objectScale = 1.0) {
    this.scene = scene;
    this.objectScale = objectScale;
    this.cloudSprites = [];
    
    // Determine battlefield dimensions (don't multiply by objectScale - these are world coordinates)
    const isLarge = battlefieldSize === 'large' || battlefieldSize === 'largew';
    this.battlefieldWidth = battlefieldSize === 'largew' ? 4906 : (isLarge ? 4096 : 2048);
    this.battlefieldHeight = battlefieldSize === 'largew' ? 2304 : (isLarge ? 4096 : 2048);
    
    // Cloud spawn timer - spawn a new cloud every 8-15 seconds (less frequent since we need fewer)
    this.cloudSpawnTimer = 0;
    this.cloudSpawnInterval = Phaser.Math.Between(8000, 15000);
    //this.cloudSpawnInterval = Phaser.Math.Between(2000, 4000);
    
    // Determine wind direction for this match (all clouds move in same direction)
    // Bias toward right/down for natural feel
    const windDirectionX = Math.random() > 0.3 ? 1 : -1; // 70% right, 30% left
    const windDirectionY = Math.random() > 0.5 ? 1 : -1; // 50% down, 50% up
    this.windVelocityX = Phaser.Math.FloatBetween(0.08, 0.16) * windDirectionX;
    this.windVelocityY = Phaser.Math.FloatBetween(0.04, 0.08) * windDirectionY;
    
    console.log(`[CloudManager] Initialized with wind velocity: (${this.windVelocityX.toFixed(3)}, ${this.windVelocityY.toFixed(3)})`);
  }
  
  /**
   * Initialize the cloud system with starting clouds
   */
  init() {
    // Start with 2-3 initial clouds (fewer than before since we're using textures)
    const initialClouds = Phaser.Math.Between(1, 3);
    for (let i = 0; i < initialClouds; i++) {
      this.spawnCloud();
    }
    console.log(`[CloudManager] Created ${initialClouds} initial clouds`);
  }
  
  /**
   * Spawn a new cloud sprite with random scale and rotation
   */
  spawnCloud() {
    // Random scale variation (smaller to larger, apply objectScale for visual consistency)
    const scale = Phaser.Math.FloatBetween(2.8, 5.8) * Math.min(this.objectScale, 1.0);
    
    // Random rotation for variety
    const rotation = Phaser.Math.FloatBetween(-0.8, 0.8);
    
    // Random opacity
    const opacity = Phaser.Math.FloatBetween(0.15, 0.30);
    
    // Use shared wind velocity for all clouds (with slight variation in speed)
    const speedVariation = Phaser.Math.FloatBetween(0.8, 1.1); // ±10% speed variation
    const velocityX = this.windVelocityX * speedVariation;
    const velocityY = this.windVelocityY * speedVariation;
    
    // Spawn from edges based on wind direction
    let startX, startY;
    const cloudSize = 400 * scale; // Approximate cloud texture size for boundary calculations
    
    if (Math.abs(velocityX) > Math.abs(velocityY)) {
      // Horizontal movement dominant - spawn from opposite edge
      if (velocityX > 0) {
        startX = -cloudSize - 100; // Moving right, start from left
        startY = Phaser.Math.Between(-cloudSize, this.battlefieldHeight + cloudSize);
      } else {
        startX = this.battlefieldWidth + cloudSize + 100; // Moving left, start from right
        startY = Phaser.Math.Between(-cloudSize, this.battlefieldHeight + cloudSize);
      }
    } else {
      // Vertical movement dominant - spawn from opposite edge
      if (velocityY > 0) {
        startY = -cloudSize - 100; // Moving down, start from top
        startX = Phaser.Math.Between(-cloudSize, this.battlefieldWidth + cloudSize);
      } else {
        startY = this.battlefieldHeight + cloudSize + 100; // Moving up, start from bottom
        startX = Phaser.Math.Between(-cloudSize, this.battlefieldWidth + cloudSize);
      }
    }
    
    // Create cloud sprite
    const cloudSprite = this.scene.add.sprite(startX, startY, 'cloud1');
    cloudSprite.setDepth(15); // Above players but below UI
    cloudSprite.setScale(scale);
    cloudSprite.setRotation(rotation);
    cloudSprite.setAlpha(opacity);
    // Don't tint - texture already has good transparency/shading built in
    cloudSprite.setBlendMode(Phaser.BlendModes.NORMAL);
    
    // Store cloud data for animation
    cloudSprite.cloudData = {
      velocityX: velocityX,
      velocityY: velocityY,
      cloudSize: cloudSize,
      // Boundaries for deletion (larger buffer for smooth exit)
      minX: -cloudSize - 200,
      maxX: this.battlefieldWidth + cloudSize + 200,
      minY: -cloudSize - 200,
      maxY: this.battlefieldHeight + cloudSize + 200
    };
    
    this.cloudSprites.push(cloudSprite);
    
    // UI camera should ignore world clouds
    if (this.scene.uiCamera) {
      this.scene.uiCamera.ignore(cloudSprite);
    }
    
    //console.log(`[CloudManager] Spawned cloud at (${Math.round(startX)}, ${Math.round(startY)}) with scale ${scale.toFixed(2)}, alpha ${opacity.toFixed(2)}. Total clouds: ${this.cloudSprites.length}`);
  }
  
  /**
   * Update all clouds - move them and spawn new ones periodically
   */
  update(delta) {
    if (!this.cloudSprites) return;
    
    // Update spawn timer and create new clouds periodically
    this.cloudSpawnTimer += delta;
    if (this.cloudSpawnTimer >= this.cloudSpawnInterval) {
      this.spawnCloud();
      this.cloudSpawnTimer = 0;
      // Randomize next spawn interval (8-15 seconds)
      this.cloudSpawnInterval = Phaser.Math.Between(8000, 15000);
    }
    
    // Update existing clouds
    for (let i = this.cloudSprites.length - 1; i >= 0; i--) {
      const cloudSprite = this.cloudSprites[i];
      const data = cloudSprite.cloudData;
      
      // Update position
      cloudSprite.x += data.velocityX * delta * 0.3;
      cloudSprite.y += data.velocityY * delta * 0.3;
      
      // Check if cloud is offscreen - delete it
      let isOffscreen = false;
      if (cloudSprite.x > data.maxX || cloudSprite.x < data.minX || 
          cloudSprite.y > data.maxY || cloudSprite.y < data.minY) {
        isOffscreen = true;
      }
      
      if (isOffscreen) {
        // Remove from array and destroy
        //console.log(`[CloudManager] Cloud moved offscreen at (${Math.round(cloudSprite.x)}, ${Math.round(cloudSprite.y)}). Deleting. Remaining: ${this.cloudSprites.length - 1}`);
        cloudSprite.destroy();
        this.cloudSprites.splice(i, 1);
      }
    }
  }
  
  /**
   * Clean up all clouds
   */
  destroy() {
    if (this.cloudSprites) {
      this.cloudSprites.forEach(cloud => {
        if (cloud && cloud.destroy) {
          cloud.destroy();
        }
      });
      this.cloudSprites = [];
    }
  }
}

export default CloudManager;

